/**
 * Плитка референсов: картинка, выделение, «в корзине», заметка, быстрые действия.
 *
 * Общая для экрана библиотеки, «Всех референсов» и корзины: что показывать
 * (подпись библиотеки) решает владелец флагами, а данные и мутации даёт он же —
 * сетка ничего не знает о кэше. Рисуется порциями по `PAGE`: следующую просит метка
 * под сеткой. К первой порции возвращает владелец — пересозданием сетки (`key` по виду,
 * папке и фильтрам), поэтому и первый кадр после смены рисуется уже с новой порцией;
 * перерисовка с новыми `items` при том же ключе порцию не трогает. `startLimit` —
 * сколько плиток нарисовать сразу (возврат из папки к тому же месту). `scores` — выдача
 * поиска по фото: процент сходства на плитке фото и папки (у папки — её первое, лучшее фото).
 * Выделение сквозное, в порядке экрана (`folderLevelShown`): сначала обложки папок (индекс —
 * место плитки), потом россыпь — индекс фото сдвинут на число обложек.
 */
import { Check, ShoppingBasket, SquareArrowOutUpRight, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { Link } from "react-router";

import { ModelFolderTile } from "@/components/library/ModelFolderTile";
import { NoteEditor } from "@/components/library/NoteEditor";
import { TileTags } from "@/components/library/TileTags";
import { MoreOnScroll, PAGE } from "@/components/trends/MoreOnScroll";
import { RetryImg } from "@/components/trends/PinGrid";
import { btnGhost, ErrorLine } from "@/components/trends/ui";
import { plural, verdictLabel, type ReferenceItem, type Verdict } from "@/lib/library";
import { mediaUrl, num, thumbUrl } from "@/lib/media";
import type { ModelFolder } from "@/lib/models";
import { scoreLabel } from "@/lib/similar";

export type ReferenceGridProps = {
  items: ReferenceItem[];
  selected: ReadonlySet<number>;
  selecting: boolean;
  onToggle: (index: number, range: boolean) => void;
  tagNames: ReadonlyMap<number, string>;
  showLibrary: boolean;
  onBasket: (id: number, add: boolean) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  /** `null` снимает вердикт. */
  onVerdict: (id: number, verdict: Verdict | null) => Promise<void>;
  /** Сколько плиток нарисовать при создании сетки; по умолчанию одна порция. */
  startLimit?: number;
  folders?: ModelFolder<ReferenceItem>[];
  /** `limit` — сколько плиток нарисовано сейчас: владелец запоминает место до возврата. */
  onOpenFolder?: (key: string, limit: number) => void;
  scores?: ReadonlyMap<number, number>;
  /** `false` — без бейджей процента (выдача по описанию: оценки есть, показывать нечего). */
  percent?: boolean;
};

export function ReferenceGrid({ items, folders, onOpenFolder, startLimit, scores, percent = true, ...shared }: ReferenceGridProps) {
  const [limit, setLimit] = useState(startLimit ?? PAGE);
  const more = useCallback(() => setLimit((l) => l + PAGE), []);
  const folderCount = folders?.length ?? 0;
  const total = folderCount + items.length;
  const drawnFolders = (folders ?? []).slice(0, limit);
  // Папки из `groupByModel` непустые (создаются с первым фото): индекс папки = индекс её обложки в выделении.
  const coverCount = folderCount;
  const drawnItems = items.slice(0, Math.max(0, limit - folderCount));
  const drawn = drawnFolders.length + drawnItems.length;
  return (
    <>
      <div
        className={`grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3 p-3 ${shared.selected.size ? "pb-20" : ""}`}
        data-annot="референсы"
      >
        {drawnFolders.map((folder, folderIndex) => (
          <ModelFolderTile
            key={`folder:${folder.key}`}
            folder={folder}
            onOpen={() => onOpenFolder?.(folder.key, limit)}
            index={folderIndex}
            selected={shared.selected}
            selecting={shared.selecting}
            onToggle={shared.onToggle}
            tagNames={shared.tagNames}
            percent={percent}
            {...(scores && folder.items[0] ? { score: scores.get(folder.items[0].id) } : {})}
          />
        ))}
        {drawnItems.map((item, index) => (
          <ReferenceTile key={item.id} item={item} index={coverCount + index} score={percent ? scores?.get(item.id) : undefined} {...shared} />
        ))}
      </div>
      {drawn < total ? (
        <MoreOnScroll key={limit} onMore={more} text={`показано ${num(drawn)} из ${num(total)}`} />
      ) : null}
    </>
  );
}

function ReferenceTile({
  item,
  index,
  score,
  selected,
  selecting,
  onToggle,
  showLibrary,
  onBasket,
  onDelete,
  onVerdict,
  tagNames,
}: { item: ReferenceItem; index: number; score: number | undefined } & Omit<
  ReferenceGridProps,
  "items" | "startLimit" | "folders" | "onOpenFolder" | "scores"
>) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const isSelected = selected.has(item.id);
  const boxVisible = isSelected || selecting || selected.size > 0;
  const ratio = item.asset_width && item.asset_height ? `${item.asset_width} / ${item.asset_height}` : "3 / 4";

  const run = async (job: Promise<void>) => {
    setPending(true);
    setError(null);
    try {
      await job;
    } catch (exc) {
      setError(exc);
    } finally {
      setPending(false);
    }
  };

  const askDelete = () => {
    const used = item.used ? ` и из ${item.used} ${plural(item.used, "проекта", "проектов", "проектов")}` : "";
    const gens =
      item.generations > 0
        ? `; ${item.generations} ${plural(item.generations, "генерация останется", "генерации останутся", "генераций останутся")} в карточках платьев`
        : "";
    if (window.confirm(`Удалить референс из библиотеки? Он уйдёт из корзин${used}${gens}.`)) void run(onDelete(item.id));
  };

  return (
    <figure className={`grid min-w-0 content-start gap-1 ${pending ? "opacity-50" : ""}`} data-annot="референс">
      <div className="group relative">
        <button
          type="button"
          onClick={(e) => {
            if (selecting || e.ctrlKey || e.metaKey || e.shiftKey) onToggle(index, e.shiftKey);
            else window.open(mediaUrl(item.asset_path), "_blank");
          }}
          // Shift-клик иначе выделяет текст страницы от прошлого клика до этого.
          onMouseDown={(e) => (e.shiftKey ? e.preventDefault() : undefined)}
          className={`relative block max-h-[360px] w-full overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-subtle text-left focus-visible:outline-2 focus-visible:outline-accent ${
            isSelected ? "outline-2 outline-offset-[-2px] outline-accent" : ""
          }`}
          style={{ aspectRatio: ratio }}
          title="Открыть оригинал (Ctrl/Shift — выделить)"
        >
          <RetryImg
            src={thumbUrl(item.asset_path)}
            alt={item.note ?? ""}
            className="absolute inset-0 h-full w-full object-cover"
          />
          {item.verdict ? (
            <span
              className={`absolute top-1.5 left-1.5 rounded-[4px] px-1.5 text-[11px] leading-[18px] ${
                item.verdict === "works" ? "bg-ok-bg text-ok" : "bg-err-bg text-err"
              }`}
            >
              {item.verdict === "works" ? "✓" : "✗"} {verdictLabel[item.verdict]}
            </span>
          ) : null}
          {score !== undefined ? <ScoreBadge score={score} /> : null}
          {item.in_basket ? (
            <span
              className="absolute bottom-1.5 left-1.5 grid size-5 place-items-center rounded-[4px] bg-accent text-bg"
              title="Лежит в вашей корзине"
            >
              <ShoppingBasket size={12} />
            </span>
          ) : null}
        </button>
        <button
          type="button"
          aria-pressed={isSelected}
          aria-label={isSelected ? "Снять выделение" : "Выделить"}
          title="Выделить (Shift — диапазон)"
          onMouseDown={(e) => (e.shiftKey ? e.preventDefault() : undefined)}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(index, e.shiftKey);
          }}
          className={`absolute top-1.5 right-1.5 grid size-5 place-items-center rounded-[4px] border transition-opacity ${
            isSelected ? "border-accent bg-accent text-bg" : "border-white/80 bg-black/45 text-white hover:bg-black/65"
          } ${boxVisible ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"}`}
        >
          {isSelected ? <Check size={13} strokeWidth={3} /> : null}
        </button>
      </div>
      <figcaption className="grid min-w-0 gap-1 px-0.5">
        {showLibrary ? (
          <span className="truncate text-[11.5px] text-fg-faint" title={`${item.library_name} · ${item.category_name}`}>
            {item.library_name} · {item.category_name}
          </span>
        ) : null}
        <TileTags tagIds={item.tag_ids} tagNames={tagNames} autoIds={item.auto_tag_ids} />
        <NoteEditor item={item} />
        <div className="flex flex-wrap items-center gap-1">
          {(["works", "fails"] as const).map((v) => {
            const on = item.verdict === v;
            return (
              <button
                key={v}
                type="button"
                className={`${btnGhost} ${on ? (v === "works" ? "text-ok" : "text-err") : ""}`}
                disabled={pending}
                aria-pressed={on}
                title={on ? "Снять оценку" : `Оценить: ${verdictLabel[v]}`}
                onClick={() => void run(onVerdict(item.id, on ? null : v))}
              >
                {verdictLabel[v]}
              </button>
            );
          })}
          <button
            type="button"
            className={btnGhost}
            disabled={pending}
            onClick={() => void run(onBasket(item.id, !item.in_basket))}
          >
            <ShoppingBasket size={13} />
            {item.in_basket ? "убрать из корзины" : "в корзину"}
          </button>
          <Link
            to={`/lib/ref/${item.id}`}
            className={`${btnGhost} ml-auto`}
            title="Карточка референса и генерации"
            aria-label="Карточка референса и генерации"
          >
            <SquareArrowOutUpRight size={13} />
          </Link>
          <button
            type="button"
            className={`${btnGhost} hover:text-err`}
            disabled={pending}
            onClick={askDelete}
            title="Удалить референс из библиотеки"
            aria-label="Удалить референс из библиотеки"
          >
            <Trash2 size={13} />
          </button>
        </div>
        {error ? <ErrorLine error={error} /> : null}
      </figcaption>
    </figure>
  );
}

/** Процент сходства — внизу справа: слева вердикт и корзина, вверху справа выделение. */
function ScoreBadge({ score }: { score: number }) {
  return (
    <span
      className="absolute right-1.5 bottom-1.5 rounded-[4px] bg-black/60 px-1.5 text-[11px] leading-[18px] text-white tnum"
      title="Сходство с запросом"
    >
      {scoreLabel(score)}
    </span>
  );
}
